import { PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { UnitType as SharedUnitType } from "encamp-prisma/generated/browser";
import {
  WASTE_CONTAINER_TYPE_BY_CODE,
  isWasteContainerTypeCode,
} from "encamp-shared/src/constants/wasteContainerTypes";
import { canConvertWithoutDensity, convert } from "encamp-shared/src/units/unitConversions";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { APPROACHING_WINDOW_DAYS, toReportable } from "encamp-waste-accumulation";
import {
  ClockStatus,
  ContainerEventType,
  FillEventSource,
  ReconcileChoice,
  UnitType,
  WasteAccumulationAreaType,
  WasteContainerStatus,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { formatDateLikeForDisplay } from "util/dates";

export const NO_VALUE_PLACEHOLDER = "";

/**
 * Stand-in for a half of an "amount / capacity" pair we cannot state.
 *
 * Deliberately not `NO_VALUE_PLACEHOLDER`, which is empty: a blank reads fine
 * as a whole cell but inside a pair leaves a dangling "/ 55 gals" that looks
 * like a rendering fault.
 */
const UNKNOWN_PAIR_HALF = "—";

/** Longer unit labels for form selects and the detail page. */
export const UNIT_LABELS: Record<UnitType, string> = {
  [UnitType.Barrels]: "Barrels",
  [UnitType.CubicFeet]: "Cubic feet",
  [UnitType.Gallons]: "Gallons",
  [UnitType.Kilograms]: "Kilograms",
  [UnitType.Liters]: "Liters",
  [UnitType.MetricTons]: "Metric tons",
  [UnitType.Pounds]: "Pounds",
  [UnitType.Tons]: "Tons",
};

/**
 * Value-identical lookup from the shared (Prisma-derived) unit strings to the
 * generated GraphQL enum, so pick-list defaults can flow into form state
 * without casting across the two nominal types.
 */
const GRAPHQL_UNIT_BY_VALUE: Record<string, UnitType> = Object.fromEntries(
  Object.values(UnitType).map((unit) => [unit, unit])
);

export function toGraphqlUnitType(unit: string | null | undefined): UnitType | null {
  if (!unit) {
    return null;
  }
  return GRAPHQL_UNIT_BY_VALUE[unit] ?? null;
}

/** The reverse lookup, for handing GraphQL enum values to shared unit math. */
const SHARED_UNIT_BY_VALUE: Record<string, SharedUnitType> = Object.fromEntries(
  Object.values(SharedUnitType).map((unit) => [unit, unit])
);

function toSharedUnitType(unit: string | null | undefined): SharedUnitType | null {
  if (!unit) {
    return null;
  }
  return SHARED_UNIT_BY_VALUE[unit] ?? null;
}

/** Formats via the app's shared helper ("1,500 gals"); em dash when unset. */
export function formatQuantity(
  amount: number | null | undefined,
  unit: UnitType | null | undefined
): string {
  if (amount == null) {
    return NO_VALUE_PLACEHOLDER;
  }
  return prettyPrintQuantity({ amount, unit, includeUnit: !!unit, abbreviateUnit: true });
}

/**
 * "30 / 55 GAL" when the amount and capacity share a unit, otherwise each
 * quantity keeps its own suffix ("300 LBS / 55 GAL").
 */
export function formatAmountCapacity({
  amount,
  amountUnit,
  capacity,
  capacityUnit,
  amountUnavailable,
}: {
  amount?: number | null;
  amountUnit?: UnitType | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
  /**
   * Set from the field of the same name: a null `amount` is an UNKNOWN total
   * rather than an empty container. Omitted by callers holding no saved
   * container — a form has no events, so its blank amount is a zero.
   */
  amountUnavailable?: boolean | null;
}): string {
  if (amount == null && capacity == null) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (capacity == null) {
    return formatQuantity(amount, amountUnit);
  }
  if (amount == null) {
    // A null total is two different states, and only the API can tell them
    // apart: a container with no fill events is empty, while one whose events
    // are an unsummable mass+volume mix holds an unknown quantity that may be
    // a full drum. Saying "0" for the second reports waste on site as absent.
    if (amountUnavailable) {
      return `${UNKNOWN_PAIR_HALF} / ${formatQuantity(capacity, capacityUnit)}`;
    }
    return `0 / ${formatQuantity(capacity, capacityUnit)}`;
  }
  if (amountUnit === capacityUnit) {
    const bareAmount = prettyPrintQuantity({ amount, includeLabel: false });
    return `${bareAmount} / ${formatQuantity(capacity, capacityUnit)}`;
  }
  return `${formatQuantity(amount, amountUnit)} / ${formatQuantity(capacity, capacityUnit)}`;
}

/**
 * The raw amount ÷ capacity ratio, in the capacity's unit. Null when it
 * can't be expressed as a ratio at all (no capacity, no recorded total, or
 * units that need a density to convert).
 *
 * Deliberately unclamped: a caller that needs to tell an over-filled
 * container from a merely full one — to color it, or to flag it — cannot do
 * that from `containerFillFraction`, which flattens both to 1.
 */
export function containerFillRatio(container: {
  amount?: number | null;
  amountUnit?: UnitType | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
}): number | null {
  const { amount, amountUnit, capacity, capacityUnit } = container;
  if (amount == null || capacity == null || capacity <= 0) {
    return null;
  }
  const amountUnitShared = toSharedUnitType(amountUnit);
  const capacityUnitShared = toSharedUnitType(capacityUnit);
  if (
    !amountUnitShared ||
    !capacityUnitShared ||
    !canConvertWithoutDensity(amountUnitShared, capacityUnitShared)
  ) {
    return null;
  }
  const inCapacityUnit = convert(amount, amountUnitShared, capacityUnitShared, null, null);
  if (inCapacityUnit === null) {
    return null;
  }
  return inCapacityUnit / capacity;
}

/**
 * How full the container is, 0–1, for the fill-level gauge. Clamped so an
 * over-filled container reads as full rather than overflowing the graphic;
 * reach for `containerFillRatio` when the overflow itself is the point.
 */
export function containerFillFraction(container: {
  amount?: number | null;
  amountUnit?: UnitType | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
}): number | null {
  const ratio = containerFillRatio(container);
  return ratio === null ? null : Math.min(1, Math.max(0, ratio));
}

/** Count of adds vs removes for the fill card's "N adds • M removes" line. */
export function countAddsAndRemoves(events: ReadonlyArray<{ amount: number }>): {
  adds: number;
  removes: number;
} {
  let adds = 0;
  let removes = 0;
  for (const event of events) {
    if (event.amount > 0) {
      adds += 1;
    } else if (event.amount < 0) {
      removes += 1;
    }
  }
  return { adds, removes };
}

export const CONTAINER_STATUS_LABELS: Record<WasteContainerStatus, string> = {
  [WasteContainerStatus.Staged]: "Staged",
  [WasteContainerStatus.Accumulating]: "Accumulating",
  [WasteContainerStatus.Full]: "Full",
  [WasteContainerStatus.PendingPickup]: "Pending pickup",
  [WasteContainerStatus.Shipped]: "Shipped",
};

/** Human label for a DOT container type code, falling back to the raw code. */
export function containerTypeLabel(code: string | null | undefined): string {
  if (!code) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (isWasteContainerTypeCode(code)) {
    return WASTE_CONTAINER_TYPE_BY_CODE[code].label;
  }
  return code;
}

/**
 * "Central / Dock A" — the kind of area first, then which one.
 *
 * The kind is what decides which rules the container is under, so it leads;
 * two facilities can both have a "Dock A" and only the prefix says whether
 * either is running a 180-day clock or a volume threshold. Shared so the
 * list column and the drawer's header line say it the same way.
 */
export function accumulationAreaLabel(
  area: { name: string; type: WasteAccumulationAreaType } | null | undefined
): string {
  if (!area) {
    return NO_VALUE_PLACEHOLDER;
  }
  const kind = area.type === WasteAccumulationAreaType.Central ? "Central" : "Satellite";
  return `${kind} / ${area.name}`;
}

export function formatContainerDate(isoDateStr: string | null | undefined): string {
  if (!isoDateStr) {
    return NO_VALUE_PLACEHOLDER;
  }
  return formatDateLikeForDisplay(isoDateStr) || NO_VALUE_PLACEHOLDER;
}

/**
 * "September 16, 2026" — the long form the waste specs use for the dates a
 * reader is asked to reason about rather than scan past: a container's
 * deadline, and the day each fill event happened.
 *
 * Distinct from `formatLabelDate` below, which matches what the PDF prints,
 * and from `formatContainerDate`'s short "01/01/2026".
 */
export function formatContainerDateLong(isoDateStr: string | null | undefined): string {
  if (!isoDateStr) {
    return NO_VALUE_PLACEHOLDER;
  }
  return formatDateLikeForDisplay(isoDateStr, { format: "MMMM d, yyyy" }) || NO_VALUE_PLACEHOLDER;
}

/**
 * "Jan 1, 2026" -- the printed container label's date format (see the
 * pdf-generator lambda's `formatUtcDate`). Kept distinct from
 * `formatContainerDate`'s "01/01/2026", which is this app's general-purpose
 * short date and isn't what actually prints on the label; the live preview
 * needs to match the PDF, not the rest of the UI.
 */
export function formatLabelDate(isoDateStr: string | null | undefined): string {
  if (!isoDateStr) {
    return NO_VALUE_PLACEHOLDER;
  }
  return formatDateLikeForDisplay(isoDateStr, { format: "MMM d, yyyy" }) || NO_VALUE_PLACEHOLDER;
}

/**
 * "55 gal Drum, barrel, or keg (metal)" for the label preview's CONTAINER
 * field -- mirrors the pdf-generator lambda's `containerTypeDescription` so
 * the preview shows the same secondary clause the printed label does.
 * `undefined` (no clause) when neither the type nor the capacity is known,
 * matching the PDF's behavior of omitting an empty clause rather than
 * printing an empty string or a lone separator.
 */
export function containerCapacityAndTypeDescription({
  containerType,
  capacity,
  capacityUnit,
}: {
  containerType?: string | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
}): string | undefined {
  const typeLabel = containerTypeLabel(containerType) || undefined;
  const capacityText =
    capacity != null && capacityUnit
      ? prettyPrintQuantity({ amount: capacity, unit: capacityUnit, abbreviateUnit: true })
      : undefined;
  const description = [capacityText, typeLabel].filter(Boolean).join(" ");
  return description.length > 0 ? description : undefined;
}

/** Labels for the accumulation clock's read-derived status (list chip, detail rows, filter). */
export const CLOCK_STATUS_LABELS: Record<ClockStatus, string> = {
  [ClockStatus.Ok]: "OK",
  [ClockStatus.Approaching]: "Approaching",
  [ClockStatus.OverLimit]: "Over limit",
  [ClockStatus.Undetermined]: "Not determined",
  [ClockStatus.Overdue]: "Overdue",
  [ClockStatus.None]: "None",
  [ClockStatus.StatusNeeded]: "Status needed",
};

/**
 * "12 days left" / "3 days overdue" for a TIME limit's `daysRemaining`; the
 * placeholder for a VOLUME limit, which carries no deadline (and so no
 * daysRemaining) of its own.
 */
export function formatDaysRemaining(daysRemaining: number | null | undefined): string {
  if (daysRemaining == null) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (daysRemaining < 0) {
    return `${pluralize("day", -daysRemaining, true)} overdue`;
  }
  return `${pluralize("day", daysRemaining, true)} left`;
}

/**
 * Reportable pounds for the detail page's amount display, via the shared
 * clock engine's `toReportable`. `specificGravity` is always null for
 * now — stream density is a later project (Project 2) — so a volume amount
 * (or barrels, which need a density even to convert to another volume
 * unit) can't be expressed in pounds yet and this returns null; the caller
 * shows the "specific gravity needed" hint instead of a number.
 */
export function reportablePounds(
  amount: number | null | undefined,
  unit: UnitType | null | undefined
): number | null {
  if (amount == null || !unit) {
    return null;
  }
  const sharedUnit = toSharedUnitType(unit);
  if (!sharedUnit) {
    return null;
  }
  return toReportable(amount, sharedUnit, null).lb;
}

/** "≈ 1,500 lb" — full numbers, thousands-separated; waste module rule: never K/M abbreviations. */
export function formatReportablePounds(lb: number): string {
  return `≈ ${Math.round(lb).toLocaleString()} lb`;
}

/**
 * Picker shortcuts for "when did this happen" fields: capture-first means
 * most entries are today's or yesterday's work.
 */
export const OCCURRED_AT_SHORTCUTS: PickersShortcutsItem<DateTime>[] = [
  { label: "Today", getValue: () => DateTime.local() },
  { label: "Yesterday", getValue: () => DateTime.local().minus({ days: 1 }) },
];

/**
 * Whether a picked date is after the user's local today — the timezone-aware
 * "no future date" check for the fill dialogs. This is the authoritative guard
 * (the server only knows a UTC-midnight value and can't recover the user's
 * calendar day, so it keeps a looser tolerance). Compares calendar days so any
 * time today is allowed and only a strictly-later day is rejected.
 */
export function isLocalDateInFuture(dateIso: string | null | undefined): boolean {
  if (!dateIso) {
    return false;
  }
  return DateTime.fromISO(dateIso).startOf("day") > DateTime.local().startOf("day");
}

/**
 * The client face of the server's episodic fill-window guard: whether a
 * date-only value lands outside [event start, 60-day deadline], both ends
 * inclusive. ISO day strings compare lexically, matching the server's
 * UTC-midnight flooring.
 */
export function isOutsideEpisodicWindow(
  dateIso: string,
  eventStartDate: string,
  eventAccumulationDeadline: string
): boolean {
  const day = dateIso.slice(0, 10);
  return day < eventStartDate.slice(0, 10) || day > eventAccumulationDeadline.slice(0, 10);
}

/** Select items for every amount/unit pair in the container forms. */
export const UNIT_SELECT_ITEMS = Object.values(UnitType).map((unit) => ({
  value: unit,
  display: UNIT_LABELS[unit],
}));

/** "+30 gals" / "−10 gals" — the sign is the direction, so always show it. */
export function formatSignedQuantity(amount: number, unit: UnitType | null | undefined): string {
  const magnitude = prettyPrintQuantity({
    amount: Math.abs(amount),
    unit,
    includeUnit: !!unit,
    abbreviateUnit: true,
  });
  return `${amount < 0 ? "−" : "+"}${magnitude}`;
}

export const FILL_EVENT_SOURCE_LABELS: Record<FillEventSource, string> = {
  [FillEventSource.Manual]: "Manual",
  [FillEventSource.Imported]: "Imported",
  [FillEventSource.AiExtracted]: "AI extracted",
  [FillEventSource.ReconciledFill]: "Reconciled fill",
  [FillEventSource.Shipment]: "Shipment",
};

export const CONTAINER_EVENT_TYPE_LABELS: Record<ContainerEventType, string> = {
  [ContainerEventType.Created]: "Created",
  [ContainerEventType.Moved]: "Moved",
  [ContainerEventType.StatusChanged]: "Status changed",
  [ContainerEventType.Consolidated]: "Consolidated",
  [ContainerEventType.Reconciled]: "Reconciled",
  [ContainerEventType.Emptied]: "Emptied",
  [ContainerEventType.Shipped]: "Shipped",
  [ContainerEventType.Reclassified]: "Waste stream reassigned",
};

export const RECONCILE_CHOICE_LABELS: Record<ReconcileChoice, string> = {
  [ReconcileChoice.AddEvent]: "I'll log the missing fill event myself",
  [ReconcileChoice.AutoFill]: "Top the container up to capacity automatically",
  [ReconcileChoice.KeepAsIs]: "Keep as-is — the container stays visibly underfilled",
  [ReconcileChoice.AdjustCapacity]: "I'll correct the container's capacity instead",
};

/**
 * The lifecycle transitions the status form offers. Mirrors the transition
 * matrix in encamp-waste-data-access's containerRules (which the mutation
 * and its validator enforce authoritatively); duplicated here because that
 * module isn't browser-safe to import.
 */
export const STATUS_TRANSITION_OPTIONS: Record<WasteContainerStatus, WasteContainerStatus[]> = {
  [WasteContainerStatus.Staged]: [WasteContainerStatus.Accumulating],
  [WasteContainerStatus.Accumulating]: [
    WasteContainerStatus.Full,
    WasteContainerStatus.PendingPickup,
  ],
  [WasteContainerStatus.Full]: [
    WasteContainerStatus.Accumulating,
    WasteContainerStatus.PendingPickup,
  ],
  [WasteContainerStatus.PendingPickup]: [
    WasteContainerStatus.Accumulating,
    WasteContainerStatus.Full,
  ],
  [WasteContainerStatus.Shipped]: [],
};

/**
 * Whether the drawer should offer "Request manifest" for a container, and if
 * not, why.
 *
 * Mirrors `containerNotShippableViolation` in encamp-waste-data-access, which
 * is what the packaging mutation actually enforces — the button used to send
 * every container to the builder and let the validator reject it there, which
 * meant learning the rule only after leaving the page.
 *
 * The split between hiding and disabling is whether the reader can do anything
 * about it from here. A container that has shipped, or is already packaged onto
 * an open shipment, is finished with this action — the shipping card links to
 * the shipment instead. A container in a satellite area or with nothing in it
 * is one action away, and both of those actions are buttons in this same
 * header, so the offer stays visible with the reason attached.
 */
export type RequestManifestAvailability =
  | { kind: "allowed" }
  | { kind: "hidden" }
  | { kind: "blocked"; reason: string };

export function requestManifestAvailability(container: {
  status: WasteContainerStatus;
  amount?: number | null;
  accumulationArea?: { type: WasteAccumulationAreaType } | null;
  openShipments: ReadonlyArray<unknown>;
  shippedOnShipment?: unknown;
  archivedAt?: unknown;
}): RequestManifestAvailability {
  // First, as the server rule has it. The drawer happens to hide its whole
  // action row for an archived container, so today no caller can reach this
  // branch — but that makes it the caller's guarantee rather than the
  // function's, and the next caller (a row action, a bulk request over a
  // selection) would reasonably trust the answer instead of pre-filtering.
  if (container.archivedAt) {
    return { kind: "hidden" };
  }
  if (container.status === WasteContainerStatus.Shipped || container.shippedOnShipment) {
    return { kind: "hidden" };
  }
  if (container.openShipments.length > 0) {
    return { kind: "hidden" };
  }
  if (container.accumulationArea?.type !== WasteAccumulationAreaType.Central) {
    return {
      kind: "blocked",
      reason: "Move this container to central accumulation before requesting a manifest.",
    };
  }
  if (container.amount == null || container.amount <= 0) {
    return {
      kind: "blocked",
      reason:
        "Log a fill event before requesting a manifest — this container has no recorded amount.",
    };
  }
  return { kind: "allowed" };
}

/**
 * Whether the container's waste has left the site.
 *
 * The container rules in encamp-waste-data-access refuse to move a shipped
 * container or consolidate into one, and logging a removal against waste that
 * is already gone would record a second departure for the same drum — so the
 * drawer stops offering any of them. `shippedOnShipment` is checked alongside
 * the status because it is the same fact recorded from the shipment's side.
 */
export function containerHasShipped(container: {
  status: WasteContainerStatus;
  shippedOnShipment?: unknown;
}): boolean {
  return container.status === WasteContainerStatus.Shipped || !!container.shippedOnShipment;
}

/**
 * Only OVERDUE colors the reading.
 *
 * APPROACHING used to be amber, which put a warning color on most of the
 * column: the window opens 30 days out, so a container with a 90-day clock
 * spends a third of its life "approaching" and one with the satellite move
 * window is amber from the moment it starts. A color every other row wears
 * stops meaning anything, and left nothing louder for the rows that are
 * actually late. The count is the signal; `approachingNote` says on hover what
 * being inside the window means.
 */
export function clockTextColor(status: ClockStatus, overdue: boolean): string {
  if (overdue || status === ClockStatus.Overdue) {
    return "error.main";
  }
  return "text.primary";
}

/**
 * What "approaching" is measured against, for the reading's tooltip — the
 * threshold is a product decision, not something a day count reveals, and the
 * reading is no longer colored to hint at it.
 *
 * `basis` is the limit's own description as the engine wrote it ("180 days —
 * SQG, central"), so the note names WHICH deadline is close rather than
 * leaving a container with several limits ambiguous.
 */
export function approachingNote(
  status: ClockStatus,
  overdue: boolean,
  basis?: string
): string | undefined {
  if (overdue || status !== ClockStatus.Approaching) {
    return undefined;
  }
  const limit = basis ? ` (${basis})` : "";
  return `Approaching — ${APPROACHING_WINDOW_DAYS} days or fewer remain on this limit${limit}.`;
}
